import { useCallback, useEffect, useId, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Outlet,
  useLocation,
  useMatch,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  listTaskTags,
  listTasks,
  type Task,
  type TaskSort,
  type TaskSummary,
} from '../lib/tasks';
import { orderTasks } from '../lib/taskGroups';
import {
  useTaskListState,
  type CreateInBackground,
} from '../components/tasks/useTaskListState';
import {
  isTaskGrouping,
  readStoredGrouping,
  storeGrouping,
  type TaskGrouping,
} from '../lib/taskGrouping';
import { isTaskStatus, UNFINISHED_STATUSES, type TaskStatus } from '../lib/taskStatus';
import { listAssignees, type Assignee } from '../lib/assignees';
import { useAuth } from '../lib/auth';
import {
  Alert,
  Button,
  CountBadge,
  EmptyState,
  Input,
  FOCUS_RING_FLUSH,
  Select,
  SEGMENTED_TRACK,
  segmentedItem,
  SkeletonList,
} from '../components/ui';
import { TaskComposer } from '../components/tasks/TaskComposer';
import { useHideCompleted } from '../components/tasks/useHideCompleted';
import { TaskList } from '../components/tasks/TaskList';
import { BulkActionBar } from '../components/tasks/BulkActionBar';
import { SidePanel } from '../components/tasks/SidePanel';
import { TaskDetail } from '../components/tasks/TaskDetail';
import { NewTaskForm } from '../components/tasks/NewTaskForm';
import { CloseIcon, InboxIcon, PlusIcon, RefreshIcon, SearchIcon } from '../components/icons';
import { useProjectDetail } from './ProjectDetail';

/**
 * Reserved id for the unsaved-task panel. Real ids are uuids, so this can never
 * collide -- see the comment on TaskPanelRoute for why it is a param value
 * rather than its own route.
 */
const NEW_TASK = 'new';

/** What the inline add row hands to the panel when you expand it. */
type ComposeHandoff = { title: string; status: TaskStatus };

/**
 * The secondary order. Due date decides the list (see lib/taskGroups.ts), so
 * what is left for this control is the undated tail and the odd shared due
 * date -- which is most of a young project, and worth choosing. Every label
 * says so, the way the tag filter's "Any tag" frames its own list.
 *
 * `due` is the server's dueDate-then-createdAt order, so as a tiebreak it is
 * oldest-first. The value stays `due` regardless: it is the default on both
 * sides, and renaming it would break every link anyone has already sent.
 */
const SORT_LABELS: Record<TaskSort, string> = {
  due: 'Then by oldest',
  priority: 'Then by priority',
  created: 'Then by newest',
  updated: 'Then by recently updated',
  title: 'Then by title',
};

const isSort = (value: string | null): value is TaskSort => value !== null && value in SORT_LABELS;

/** Worded as full statements rather than "Status"/"None", the same as the
 *  cross-project Tasks page: the pair has to say which of the two views is on
 *  screen, not just name an axis. */
const GROUPINGS: { value: TaskGrouping; label: string }[] = [
  { value: 'status', label: 'Group by status' },
  { value: 'none', label: 'No grouping' },
];

type TasksContext = {
  tasks: TaskSummary[];
  assignees: Assignee[];
  tagSuggestions: string[];
  upsertTask: (task: TaskSummary) => void;
  removeTask: (taskId: string) => void;
  /** Post a task without waiting for it. See `useTaskListState`. */
  createInBackground: CreateInBackground;
  hrefFor: (taskId: string) => string;
};

const useTasksContext = () => useOutletContext<TasksContext>();

/**
 * The tasks tab. This is the route's own element rather than an index route,
 * because an index route unmounts the moment `:taskId` matches -- and the panel
 * is an overlay *over* the list, which has to stay mounted underneath.
 */
export function ProjectTasks() {
  const { project } = useProjectDetail();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [assignees, setAssignees] = useState<Assignee[]>([]);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);

  const { user } = useAuth();

  const assigneeId = searchParams.get('assignee') ?? '';
  const tag = searchParams.get('tag') ?? '';
  const q = searchParams.get('q') ?? '';
  const sortParam = searchParams.get('sort');
  const sort: TaskSort = isSort(sortParam) ? sortParam : 'due';

  /**
   * Finished work is hidden unless asked for.
   *
   * A project accumulates: on anything that has been running a while the Done
   * group is the longest one on the page, and it sits between the open work and
   * the bottom of the list for no reason -- a task is finished, and what is left
   * to do is the question this tab exists to answer. It is dropped at the fetch
   * rather than filtered here, so the rows never arrive.
   *
   * In the URL rather than in storage -- the same place the Tasks page and the
   * Assignees page keep theirs, and for the same reason: "everything, finished
   * included" is a view worth sending someone. Not part of `filtered` below,
   * though; hiding these is the page's resting state, not something anyone
   * turned on, and a chip offering to clear a filter nobody set would be a lie.
   */
  const showDone = searchParams.get('done') === '1';

  /**
   * The grouping preference, URL first and storage second -- the arrangement
   * the Tasks page and Projects.tsx both use. A `?group=` makes a link carry the
   * view it was sent with; without one the choice falls back to whatever this
   * person last picked, and the same key backs both task lists, because "I read
   * tasks as a flat list" is a statement about tasks and not about one page.
   *
   * The default is never written to the URL: doing so would pin it and quietly
   * override the stored preference on every later visit.
   */
  const groupParam = searchParams.get('group');
  const [storedGrouping, setStoredGrouping] = useState<TaskGrouping>(readStoredGrouping);
  const grouping: TaskGrouping = isTaskGrouping(groupParam) ? groupParam : storedGrouping;

  const setGrouping = useCallback(
    (next: TaskGrouping) => {
      setStoredGrouping(next);
      storeGrouping(next);
      updateParams({ group: next });
    },
    // `updateParams` closes over `searchParams`, and re-reading it is exactly
    // what keeps this writing onto the current filters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams],
  );

  /**
   * Everything that happens to a row -- the optimistic apply, the bulk pool,
   * the selection and its shift-click anchor -- lives in the hook, shared with
   * the cross-project Tasks page. What stays here is the four things the two
   * pages disagree about: the query, the project a create goes into, where a
   * row links to, and the URL the filters live in.
   */
  const {
    tasks,
    loading,
    refreshing,
    refresh,
    loadError,
    actionError,
    setActionError,
    failedCreates,
    dismissCreate,
    retryCreate,
    selected,
    toggleSelected,
    clearSelection,
    upsertTask,
    removeTask,
    createInBackground,
    patchTask,
    patchSelected,
    flashId,
  } = useTaskListState({
    load: (signal) =>
      listTasks(
        project.id,
        {
          ...(assigneeId ? { assigneeId } : {}),
          ...(tag ? { tag } : {}),
          ...(q ? { q } : {}),
          ...(showDone ? {} : { status: UNFINISHED_STATUSES }),
          sort,
        },
        signal,
      ),
    loadDeps: [project.id, assigneeId, tag, q, showDone, sort],
    // Deliberately not keyed on `sort` or the grouping -- both re-order the
    // same rows, and dropping the selection for that would be a surprise.
    // `showDone` is keyed on, because it decides which rows exist at all.
    resetSelectionOn: [project.id, assigneeId, tag, q, showDone],
    defaultProjectId: project.id,
    projectFor: () => ({ id: project.id, name: project.name }),
    orderFor: (entries) => orderTasks(entries, grouping),
    assignees,
    user,
  });

  // The pickers load once and do not re-filter. Both failures are silent: they
  // populate suggestions, and an empty list degrades to a usable control.
  useEffect(() => {
    const controller = new AbortController();

    void Promise.all([
      // Archived people included on purpose. The picker needs whoever is
      // already on a task even after they leave, or the select renders blank
      // and one arrow key silently unassigns it; and filtering by someone who
      // has gone is exactly the question you ask after they go.
      listAssignees({ includeArchived: true }, controller.signal).then((data) =>
        setAssignees(data.assignees),
      ),
      listTaskTags(project.id, controller.signal).then((data) => setTagSuggestions(data.tags)),
    ]).catch(() => {});

    return () => controller.abort();
  }, [project.id]);

  /**
   * The search box types locally and reaches the URL on a delay.
   *
   * It used to write `?q=` on every keystroke, and since the URL is what the
   * fetch above keys on, that was one request per character with the previous
   * one aborted underneath it -- so the list flickered through its loading
   * state while you were still typing the word you were looking for.
   *
   * The URL stays the source of truth; this is only a buffer in front of it.
   * The local value re-syncs when `q` changes from anywhere else (the Clear
   * button, the back button), and the timer is skipped when they already agree
   * so a re-render cannot schedule a write of what is already there.
   */
  const [searchDraft, setSearchDraft] = useState(q);

  useEffect(() => {
    setSearchDraft(q);
  }, [q]);

  useEffect(() => {
    if (searchDraft === q) return;
    const timer = window.setTimeout(() => updateParams({ q: searchDraft }), 200);
    return () => window.clearTimeout(timer);
    // `updateParams` closes over `searchParams`, which is exactly what should
    // re-arm this when another filter changes mid-type.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft, q, searchParams]);

  function updateParams(changes: Record<string, string>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setSearchParams(next, { replace: true });
  }

  // Every task link carries the current filters: without them, opening a task
  // would silently reset the list sitting behind the panel.
  const hrefFor = useCallback(
    (taskId: string) => `/projects/${project.id}/tasks/${taskId}${location.search}`,
    [project.id, location.search],
  );

  const filtered = assigneeId !== '' || tag !== '' || q !== '';

  // The open task's id lives in a descendant route, so useParams here would not
  // see it. Reading the path keeps the list row highlighted under the panel.
  // useMatch rather than a regex over the pathname: the regex missed a trailing
  // slash, so a hand-typed `/tasks/<id>/` rendered the panel with no row
  // highlighted behind it.
  const openTaskId = useMatch('/projects/:id/tasks/:taskId')?.params.taskId ?? null;
  // No row can be `new`, so the highlight would fall through anyway -- but
  // saying so beats relying on a lookup that quietly finds nothing.
  const activeTaskId = openTaskId === NEW_TASK ? null : openTaskId;

  /**
   * Escape drops the selection.
   *
   * Three things want this key. A popover marks it handled, which is what the
   * `defaultPrevented` check reads; and while a task panel is open Escape
   * belongs to the panel, whose own listener would otherwise fire alongside
   * this one and close two things at once.
   */
  useEffect(() => {
    if (selected.size === 0 || openTaskId !== null) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      clearSelection();
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selected.size, openTaskId, clearSelection]);

  /* Finished rows leave a few seconds after they finish, while completed work
     is hidden. Shared with the cross-project Tasks page. */
  useHideCompleted({ tasks, showDone, activeTaskId, removeTask });

  /** Opening the panel keeps the list's filters, and carries anything already
   *  typed across as router state rather than a query param -- `location.search`
   *  belongs to the filters, and a `?title=` would tangle with them. */
  const openCompose = useCallback(
    (title: string, status: TaskStatus) => {
      navigate(
        { pathname: `/projects/${project.id}/tasks/${NEW_TASK}`, search: location.search },
        { state: { title, status } satisfies ComposeHandoff },
      );
    },
    [navigate, project.id, location.search],
  );

  return (
    <div>
      {/* The toolbar, in two rows.

          The first is what the list is *asking for*: the search box takes the
          slack, the pickers keep fixed widths so the row does not reflow as
          selections change. Each control is wrapped rather than given `w-auto`
          -- the primitives carry `w-full`, which Tailwind emits after `w-auto`
          and so wins whatever order the classes are written in, leaving every
          filter on a row of its own.

          The second is everything that does not change which rows these are:
          how they are laid out, and the two actions. Split deliberately rather
          than left to `flex-wrap` to break where it happens to run out of room
          -- with five filters on the row, the wrap point moved with the width
          of a project's tag list, so the same page put the grouping control on
          a different line at a different zoom. A rule you can state beats a
          line break you cannot predict. */}
      <div className="flex flex-wrap items-center gap-2">
        {/* The magnifier is placed over the field rather than beside it: at
            `sm:max-w-xs` this box is the narrowest it gets, and a separate icon
            column would take that width from the text. Same one-cell grid the
            Select's chevron uses. */}
        <div className="grid min-w-[10rem] flex-1 items-center [grid-template-areas:'field'] sm:max-w-xs">
          <Input
            value={searchDraft}
            placeholder="Search tasks"
            aria-label="Search tasks"
            className="pl-9 [grid-area:field]"
            onChange={(event) => setSearchDraft(event.target.value)}
          />
          <SearchIcon className="pointer-events-none ml-3 h-4 w-4 justify-self-start text-stone-400 [grid-area:field]" />
        </div>

        <div className="w-36">
          <Select
            value={assigneeId}
            aria-label="Filter by assignee"
            onChange={(event) => updateParams({ assignee: event.target.value })}
          >
            {/* "" means Anyone here and Unassigned in the task panel's picker,
                which is why this one spells Unassigned out rather than reusing
                the blank value. */}
            <option value="">Anyone</option>
            <option value="none">Unassigned</option>
            {assignees
              .filter((assignee) => !assignee.archived)
              .map((assignee) => (
                <option key={assignee.id} value={assignee.id}>
                  {assignee.name}
                </option>
              ))}
            {assignees.some((assignee) => assignee.archived) ? (
              <optgroup label="Archived">
                {assignees
                  .filter((assignee) => assignee.archived)
                  .map((assignee) => (
                    <option key={assignee.id} value={assignee.id}>
                      {assignee.name}
                    </option>
                  ))}
              </optgroup>
            ) : null}
            {/* A hard-deleted id would otherwise leave the box blank over an
                empty list, with nothing on screen saying a filter is on. */}
            {assigneeId && assigneeId !== 'none' && !assignees.some((a) => a.id === assigneeId) ? (
              <option value={assigneeId}>Unknown assignee</option>
            ) : null}
          </Select>
        </div>

        <div className="w-36">
          <Select
            value={tag}
            aria-label="Filter by tag"
            onChange={(event) => updateParams({ tag: event.target.value })}
          >
            <option value="">Any tag</option>
            {tagSuggestions.map((suggestion) => (
              <option key={suggestion} value={suggestion}>
                {suggestion}
              </option>
            ))}
          </Select>
        </div>

        {/* The house toggle, worded and shaped exactly as the Tasks page's and
            the Assignees page's archived one: a button whose label says what
            the next click does, with `aria-pressed` carrying the state a
            sighted reader gets from the word itself. Not a segmented pair --
            "show" and "hide" are one thing on or off, not two views to choose
            between, which is what the grouping control further along is. */}
        <Button aria-pressed={showDone} onClick={() => updateParams({ done: showDone ? '' : '1' })}>
          {showDone ? 'Hide completed' : 'Show completed'}
        </Button>

        <div className="w-56">
          <Select
            value={sort}
            aria-label="Secondary sort"
            onChange={(event) => updateParams({ sort: event.target.value })}
          >
            {(Object.keys(SORT_LABELS) as TaskSort[]).map((option) => (
              <option key={option} value={option}>
                {SORT_LABELS[option]}
              </option>
            ))}
          </Select>
        </div>

      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {/* The house segmented control, the same one the Tasks page, the
            dashboard's due window and the projects card/list switch use. Two
            labelled buttons rather than a switch: "grouped" and "flat" are two
            views, and a bare toggle would not say which one is on screen.

            It leads the second row because it is the only control on the page
            that changes nothing about *what* is in the list, only how it is
            arranged -- the same thing the two actions beside it can be said to
            do, and the reason they share a row. */}
        <div role="group" aria-label="Grouping" className={SEGMENTED_TRACK}>
          {GROUPINGS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              aria-pressed={grouping === value}
              onClick={() => setGrouping(value)}
              className={segmentedItem(grouping === value)}
            >
              {label}
            </button>
          ))}
        </div>

        {/* The two actions sit together at the far end of the same row:
            neither changes what the list is asking for. Refresh only goes and
            asks again -- every edit here is optimistic and nothing polls, so a
            tab left open is the one place these rows can be wrong, and
            reloading the browser to fix that throws the filters away. The icon
            drops out while the request is in the air and the Spinner takes its
            place, so the button keeps its width.

            One `ml-auto` box rather than two flex items with the `ml-auto` on
            Refresh: on a narrow enough screen this row wraps too, and as
            separate items the primary action was the one that broke to a line
            of its own, left-aligned under the grouping control while Refresh
            stayed up on the right. As a pair they move together. */}
        <div className="ml-auto flex items-center gap-2">
          <Button loading={refreshing} disabled={loading} onClick={refresh}>
            {refreshing ? null : <RefreshIcon />}
            Refresh
          </Button>

          <Button variant="primary" onClick={() => openCompose('', 'TODO')}>
            <PlusIcon />
            New task
          </Button>
        </div>
      </div>

      {/*
        The control the empty state was already telling people to use.
        "Try clearing the filters above" pointed at nothing -- clearing meant
        resetting three separate pickers by hand -- and with the filters set but
        no matches, the row of controls gives no hint that any of them is on.
      */}
      {filtered ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-stone-500">
          <span>Filtered by</span>
          {q ? <FilterChip onClear={() => updateParams({ q: '' })}>“{q}”</FilterChip> : null}
          {assigneeId ? (
            <FilterChip onClear={() => updateParams({ assignee: '' })}>
              {assigneeId === 'none'
                ? 'Unassigned'
                : (assignees.find((entry) => entry.id === assigneeId)?.name ?? 'Unknown assignee')}
            </FilterChip>
          ) : null}
          {tag ? <FilterChip onClear={() => updateParams({ tag: '' })}>#{tag}</FilterChip> : null}
          <Button
            compact
            variant="ghost"
            onClick={() => updateParams({ q: '', assignee: '', tag: '' })}
          >
            Clear all
          </Button>

          {/* Only while filtered. Unfiltered, the per-status group headings
              already give the breakdown, and a total on top of them is one
              number too many. */}
          {loading ? null : (
            <span className="ml-auto">
              <CountBadge>
                {tasks.length === 1 ? '1 match' : `${tasks.length} matches`}
              </CountBadge>
            </span>
          )}
        </div>
      ) : null}

      {failedCreates.length > 0 ? (
        <ul className="mt-4 space-y-2">
          {failedCreates.map((failure) => (
            // The shared Alert rather than its class string spelled out again.
            // This was the fourth error surface in the app and the only one with
            // no `role`, so a screen reader was told nothing at all when a
            // create failed -- which is the one failure a person walks away
            // from believing it worked.
            <li key={failure.id}>
              <Alert onDismiss={() => dismissCreate(failure.id)}>
                <span className="flex flex-wrap items-center gap-3">
                  {/* The title is the only part of the draft worth showing: it
                      is what identifies which one of several failed.
                      Capped rather than `flex-1`: a subtask can fail while the
                      panel that added it is still open, and stretching this
                      would push Try again out to the right-hand edge --
                      underneath the panel, where it cannot be clicked or even
                      seen. */}
                  <span className="min-w-0 max-w-sm truncate">
                    “{failure.input.title}” was not saved.
                  </span>
                  <Button compact onClick={() => retryCreate(failure)}>
                    Try again
                  </Button>
                </span>
              </Alert>
            </li>
          ))}
        </ul>
      ) : null}

      {actionError ? (
        <div className="mt-4">
          {/* Above the list rather than in place of it: the rows this is about
              are the ones you are looking at. */}
          <Alert onDismiss={() => setActionError(null)}>{actionError}</Alert>
        </div>
      ) : null}

      {selected.size > 0 ? (
        <BulkActionBar
          count={selected.size}
          onPatch={(patch) => void patchSelected(patch)}
          onClear={clearSelection}
        />
      ) : null}

      <div className="mt-6">
        {loadError ? (
          <Alert>{loadError}</Alert>
        ) : loading ? (
          <SkeletonList rows={6} />
        ) : tasks.length === 0 ? (
          // No groups means no inline add rows, so the empty state carries one
          // of its own rather than pointing at a control that is not there.
          <div className="space-y-4">
            <EmptyState
              icon={filtered ? <SearchIcon className="h-5 w-5" /> : <InboxIcon className="h-5 w-5" />}
              title={filtered ? 'No matching tasks' : 'No tasks yet'}
              action={
                filtered ? (
                  <Button onClick={() => updateParams({ q: '', assignee: '', tag: '' })}>
                    Clear filters
                  </Button>
                ) : null
              }
            >
              {filtered ? 'Nothing here matches all of them.' : 'Type the first one below.'}
              {showDone ? null : ' Completed tasks are hidden.'}
            </EmptyState>
            {filtered ? null : (
              <TaskComposer onSubmit={(title) => createInBackground({ title, status: 'TODO' })} />
            )}
          </div>
        ) : (
          <TaskList
            tasks={tasks}
            activeTaskId={activeTaskId}
            selectedIds={selected}
            hrefFor={hrefFor}
            flashId={flashId}
            grouping={grouping}
            onToggleSelected={toggleSelected}
            onPatch={(task, patch) => void patchTask(task, patch)}
            onAdd={(title, status) => createInBackground({ title, status })}
            onExpandCompose={openCompose}
          />
        )}
      </div>

      {/* The panel renders here but is `fixed`, so it escapes this column. */}
      <Outlet
        context={
          {
            tasks,
            assignees,
            tagSuggestions,
            upsertTask,
            removeTask,
            createInBackground,
            hrefFor,
          } satisfies TasksContext
        }
      />
    </div>
  );
}

/**
 * One active filter, with its own off switch.
 *
 * Deliberately not a `Badge`: a badge is a label, and every one of these is a
 * control. The `x` is inside the chip rather than beside it so the pair cannot
 * wrap apart from each other on a narrow toolbar.
 */
function FilterChip({ children, onClear }: { children: ReactNode; onClear: () => void }) {
  return (
    <span className="inline-flex max-w-[12rem] items-center gap-1 rounded-full bg-stone-100 py-0.5 pl-2.5 pr-1 font-medium text-stone-700">
      <span className="truncate">{children}</span>
      <button
        type="button"
        onClick={onClear}
        aria-label="Remove this filter"
        className={`rounded-full p-0.5 text-stone-500 transition hover:bg-stone-200 hover:text-stone-900 ${FOCUS_RING_FLUSH}`}
      >
        <CloseIcon />
      </button>
    </span>
  );
}

/**
 * The panel route.
 *
 * `new` is handled as a reserved value of `:taskId` rather than as its own
 * static route, even though App.tsx uses that pattern for `/projects/new`. A
 * separate route is a different element, so navigating `/tasks/new` ->
 * `/tasks/<uuid>` after Create would unmount and remount the shell and replay
 * the slide-in as a visible flash, on the most important flow in the feature.
 *
 * SidePanel is deliberately left unkeyed for the same reason: it survives both
 * that transition and switching between two tasks, so the panel animates in
 * once and then stays put. Only the body is keyed.
 */
export function TaskPanelRoute() {
  const { id, taskId } = useParams<{ id: string; taskId: string }>();
  const { tasks, assignees, tagSuggestions, upsertTask, removeTask, createInBackground, hrefFor } =
    useTasksContext();
  const navigate = useNavigate();
  const location = useLocation();
  const titleId = useId();

  if (!taskId) return null;

  const close = () => navigate({ pathname: `/projects/${id}/tasks`, search: location.search });

  // Router state is only ever absent (a cold reload of /tasks/new drops it), so
  // both halves fall back rather than trusting the shape.
  const handoff = (location.state ?? {}) as Partial<ComposeHandoff>;

  return (
    <SidePanel labelledBy={titleId} focusKey={taskId} onClose={close}>
      {taskId === NEW_TASK ? (
        <NewTaskForm
          key={NEW_TASK}
          titleId={titleId}
          initialTitle={typeof handoff.title === 'string' ? handoff.title : ''}
          initialStatus={isTaskStatus(handoff.status ?? null) ? handoff.status! : 'TODO'}
          tasks={tasks}
          assignees={assignees}
          tagSuggestions={tagSuggestions}
          // The panel no longer follows the task it created. It cannot: the id
          // does not exist yet, and the whole point is not to wait for one.
          // Create closes back to the list, where the row is already showing.
          //
          // The project id the form hands back is ignored: no picker is shown
          // here, so the hook's default -- this project -- is already right.
          onCreate={(input) => createInBackground(input)}
        />
      ) : (
        <TaskDetail
          // Remounted per task: params change without remounting the route
          // element, so an open title editor or a half-typed comment would
          // otherwise carry over to the next task.
          key={taskId}
          taskId={taskId}
          seed={tasks.find((task) => task.id === taskId)}
          tasks={tasks}
          assignees={assignees}
          tagSuggestions={tagSuggestions}
          titleId={titleId}
          hrefFor={hrefFor}
          createInBackground={createInBackground}
          onTaskChanged={(task: Task) => upsertTask(task)}
          onTaskDeleted={removeTask}
        />
      )}
    </SidePanel>
  );
}
